import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import PageTemplate from "@/components/page-template/PageTemplate";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { useAuth } from "@/providers/AuthProvider";
import { integrationService } from "@/services/IntegrationService";
import { IntegrationDefinition } from "@/types/breeze/Integration";
import {
  integrationIcons,
  integrationBrandColors,
} from "@/components/workflow/IntegrationSelector";
import {
  Box,
  Flex,
  Text,
  Card,
  Button,
  Badge,
  Callout,
  Spinner,
  Tabs,
} from "@radix-ui/themes";
import {
  CheckCircleIcon,
  AlertCircleIcon,
  InfoIcon,
  LockIcon,
  ExternalLinkIcon,
  TrashIcon,
  RefreshCwIcon,
  CopyIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import { ConfigDialog } from "./integrations/ConfigDialog";
import { isInWrapper } from "@/utils/wrapper";

const setupGuides: Record<
  string,
  { title: string; steps: string[]; docsUrl?: string }
> = {
  slack: {
    title: "Slack App Setup",
    steps: [
      "Go to api.slack.com/apps and create a new app (or select an existing one)",
      "Under OAuth & Permissions, add these Bot Token Scopes: chat:write, chat:write.public, channels:read, groups:read, users:read, users:read.email, im:write, reactions:write, files:write",
      "Under OAuth & Permissions > Redirect URLs, add your callback URL (shown below)",
      "Copy the Client ID and Client Secret from Basic Information into the fields below",
      "Click Save & Connect to authorize the app to your Slack workspace",
    ],
    docsUrl: "https://api.slack.com/apps",
  },
  google: {
    title: "Google OAuth Setup",
    steps: [
      "Go to console.cloud.google.com and create a project (or select an existing one)",
      "Go to APIs & Services > OAuth consent screen and configure it",
      "Create OAuth 2.0 credentials under Credentials > Create Credentials > OAuth Client ID",
      "Set the application type to Web Application",
      "Add the callback URL (shown below) to Authorized redirect URIs",
      "Copy the Client ID and Client Secret into the fields below",
    ],
    docsUrl: "https://console.cloud.google.com/apis/credentials",
  },
  "google-calendar": {
    title: "Google Calendar Setup",
    steps: [
      "Uses Google OAuth credentials — configure the parent Google integration if not already done",
      "Ensure the Google Calendar API is enabled in your Google Cloud project",
    ],
    docsUrl: "https://console.cloud.google.com/apis/dashboard",
  },
  "google-analytics": {
    title: "Google Analytics Setup",
    steps: [
      "Uses Google OAuth credentials — configure the parent Google integration if not already done",
      "Enable the Google Analytics Data API and Google Analytics Admin API in your Google Cloud project",
    ],
    docsUrl: "https://console.cloud.google.com/apis/dashboard",
  },
  "google-search-console": {
    title: "Google Search Console Setup",
    steps: [
      "Uses Google OAuth credentials — configure the parent Google integration if not already done",
      "Enable the Google Search Console API in your Google Cloud project",
    ],
    docsUrl: "https://console.cloud.google.com/apis/dashboard",
  },
  github: {
    title: "GitHub OAuth App Setup",
    steps: [
      "Go to github.com/settings/developers and click New OAuth App",
      "Set the Application name and Homepage URL",
      "Set the Authorization callback URL to the callback URL shown below",
      "Copy the Client ID and generate a Client Secret, then paste both below",
      "When connecting, GitHub will prompt for the 'repo' scope (use 'public_repo' instead if you only need public repositories)",
    ],
    docsUrl: "https://github.com/settings/developers",
  },
  linkedin: {
    title: "LinkedIn OAuth Setup",
    steps: [
      "Go to linkedin.com/developers/apps and create a new app",
      "Under Auth, add the callback URL (shown below) to Authorized redirect URLs",
      "Request the Sign In with LinkedIn product under the Products tab",
      "Copy the Client ID and Client Secret from the Auth tab",
    ],
    docsUrl: "https://www.linkedin.com/developers/apps",
  },
  apple: {
    title: "Sign in with Apple Setup",
    steps: [
      "Sign in at developer.apple.com with your Apple Developer account",
      "Create a Services ID with Sign In with Apple enabled",
      "Configure the web domain and return URL (callback URL shown below)",
      "Create a key for Sign In with Apple and download it",
      "The Client ID is your Services ID, the Client Secret is a JWT signed with your key",
    ],
    docsUrl:
      "https://developer.apple.com/account/resources/identifiers/list/serviceId",
  },
};

const IntegrationsPage = withTenantAdminRequired(function IntegrationsPage() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "integrations";

  const [selectedDefinition, setSelectedDefinition] =
    useState<IntegrationDefinition | null>(null);
  const [showOAuthDialog, setShowOAuthDialog] = useState(false);

  // Hooks stay above the no-tenant return so the hook count is stable when the tenant resolves late.
  const { data: definitions = [], isLoading } = useQuery({
    queryKey: ["integration-definitions", currentTenant?.id],
    queryFn: () => integrationService.getDefinitions(),
    enabled: !!currentTenant,
  });

  const { data: connections = [] } = useQuery({
    queryKey: ["integration-connections", currentTenant?.id],
    queryFn: () => integrationService.getConnections(),
    enabled: !!currentTenant,
  });

  const oauthQueries = useQuery({
    queryKey: ["integration-oauth-all", currentTenant?.id],
    queryFn: () => integrationService.getAllIntegrationOAuthSettings(),
    enabled: !!currentTenant,
  });

  const deleteMutation = useMutation({
    mutationFn: (connectionId: string) =>
      integrationService.deleteConnection(connectionId),
    onSuccess: () => {
      toast.success("Connection removed");
      queryClient.invalidateQueries({ queryKey: ["integration-connections"] });
    },
    onError: () => toast.error("Failed to remove connection"),
  });

  if (!currentTenant) {
    return (
      <Box p="4">
        <Text size="2" color="gray">
          No organisation found
        </Text>
      </Box>
    );
  }

  const tenantId = currentTenant.id;
  const integrationCallbackUrl = `${window.location.origin}/org/${tenantId}/settings/integrations/callback`;

  // A provider can appear on both tabs (Google = sign-in + Calendar/Drive).
  const socialProviders = definitions.filter((d) => d.can_social_sign_in);
  const workflowIntegrations = definitions.filter(
    (d) => d.operations.length > 0,
  );

  const getOAuthStatus = (provider: string) => oauthQueries.data?.[provider];
  const getConnection = (defId: string) =>
    connections.find(
      (c: any) =>
        c.integration_definition_id === defId && c.is_enabled !== false,
    );

  const handleOpenConfig = (def: IntegrationDefinition) => {
    setSelectedDefinition(def);
    setShowOAuthDialog(true);
  };

  const handleAfterSave = (def: IntegrationDefinition) => {
    queryClient.invalidateQueries({ queryKey: ["integration-oauth-all"] });
    // Workflow integrations finish the connect handshake by redirecting to authorise.
    if (!(def.can_social_sign_in && def.operations.length === 0)) {
      startOAuthFlow(def.provider);
    }
  };

  const startOAuthFlow = async (provider: string) => {
    try {
      const url = await integrationService.getOAuthUrl(
        provider,
        integrationCallbackUrl,
      );
      sessionStorage.setItem(
        "pendingIntegration",
        JSON.stringify({ provider, tenantId, timestamp: Date.now() }),
      );

      if (isInWrapper()) {
        // OAuth provider pages send X-Frame-Options: DENY, so navigating the
        // iframe to them gives the user a blank "refused to connect" page.
        // Open a real top-level tab. The new tab can't make the auth'd API
        // call (Breeze's session doesn't transfer to fresh tabs), so it just
        // relays the raw `code`/`state` back here and we make the call.
        window.open(url, "_blank");
        const onMessage = async (e: MessageEvent) => {
          if (e.origin !== window.location.origin) return;
          if (e.data?.type !== "oauth-callback-raw") return;
          window.removeEventListener("message", onMessage);

          const { code, error, state } = e.data;
          if (error) {
            toast.error(`Connection failed: ${error}`);
            return;
          }
          if (!code) {
            toast.error("No authorization code received from provider.");
            return;
          }

          // Same state-parsing as IntegrationCallback: standard or PKCE-wrapped.
          let stateProvider: string | null = null;
          if (state) {
            try {
              let decoded = atob(state);
              const parts = decoded.split(":");
              if (parts.length >= 2 && isNaN(Number(parts[0]))) {
                try {
                  decoded = atob(parts[0]);
                  const innerParts = decoded.split(":");
                  if (innerParts.length >= 2) stateProvider = innerParts[1];
                } catch {
                  // fall through
                }
              }
              if (!stateProvider && parts.length >= 2) stateProvider = parts[1];
            } catch {
              console.error("Failed to decode state parameter");
            }
          }
          if (!stateProvider) {
            toast.error(
              "Could not determine the integration provider. Please try again.",
            );
            return;
          }

          try {
            await integrationService.createConnection({
              integration_definition_id: stateProvider,
              name: `${stateProvider} connection`,
              auth_code: code,
              redirect_uri: integrationCallbackUrl,
              state: state || undefined,
              is_user_connection: false,
            });
            toast.success("Integration connected");
            queryClient.invalidateQueries({
              queryKey: ["integration-connections"],
            });
          } catch (err: any) {
            const m =
              typeof err?.response?.data === "string"
                ? err.response.data
                : err?.response?.data?.error ||
                  err?.message ||
                  "Failed to complete connection.";
            toast.error(m);
          }
        };
        window.addEventListener("message", onMessage);
      } else {
        window.location.href = url;
      }
    } catch (error: any) {
      const msg =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.error ||
            error?.message ||
            "Failed to start OAuth flow";
      toast.error(msg);
    }
  };

  const handleReconnect = async (def: IntegrationDefinition) => {
    const connection = getConnection(def.id);
    if (connection) {
      await integrationService.deleteConnection(connection.id);
      queryClient.invalidateQueries({ queryKey: ["integration-connections"] });
    }
    startOAuthFlow(def.provider);
  };

  // Shared provider card for social sign-in tab
  const renderSocialCard = (def: IntegrationDefinition) => {
    const Icon = integrationIcons[def.provider];
    const status = getOAuthStatus(def.provider);
    const nativeOn = !!status?.native_flow_configured;
    const webOn = !!status?.web_flow_configured;
    const isConfigured = nativeOn || webOn;
    const bgColor = integrationBrandColors[def.provider]?.bg || "var(--gray-9)";

    return (
      <Card key={def.id}>
        <Flex justify="between" align="center">
          <Flex gap="3" align="center">
            <Box
              style={{
                width: 48,
                height: 48,
                borderRadius: 8,
                backgroundColor: bgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {Icon ? <Icon /> : <LockIcon size={24} color="white" />}
            </Box>
            <Box>
              <Text size="4" weight="bold" style={{ display: "block" }}>
                {def.display_name}
              </Text>
              <Text size="2" color="gray" style={{ display: "block" }}>
                {def.description}
              </Text>
              <Flex gap="2" mt="2">
                {isConfigured ? (
                  <>
                    <Badge
                      variant="soft"
                      color={webOn ? "green" : "gray"}
                      size="1"
                    >
                      {webOn ? (
                        <CheckCircleIcon size={12} style={{ marginRight: 4 }} />
                      ) : null}
                      Web {webOn ? "On" : "Off"}
                    </Badge>
                    <Badge
                      variant="soft"
                      color={nativeOn ? "green" : "gray"}
                      size="1"
                    >
                      {nativeOn ? (
                        <CheckCircleIcon size={12} style={{ marginRight: 4 }} />
                      ) : null}
                      Native {nativeOn ? "On" : "Off"}
                    </Badge>
                  </>
                ) : (
                  <Badge variant="soft" color="gray" size="1">
                    <AlertCircleIcon size={12} style={{ marginRight: 4 }} /> Not
                    Configured
                  </Badge>
                )}
              </Flex>
            </Box>
          </Flex>
          <Button
            size="2"
            variant={isConfigured ? "soft" : "solid"}
            onClick={() => handleOpenConfig(def)}
          >
            {isConfigured ? "Edit" : "Configure"}
          </Button>
        </Flex>
      </Card>
    );
  };

  // Shared provider card for workflow integrations tab
  const renderIntegrationCard = (def: IntegrationDefinition) => {
    const Icon = integrationIcons[def.provider];
    const oauthStatus = getOAuthStatus(def.provider);
    const isConfigured =
      oauthStatus?.has_client_id || oauthStatus?.use_social_sign_in;
    const connection = getConnection(def.id);
    const isConnected = !!connection;
    const bgColor = integrationBrandColors[def.provider]?.bg || "var(--gray-9)";

    return (
      <Card key={def.id}>
        <Flex justify="between" align="center">
          <Flex gap="3" align="center">
            <Box
              style={{
                width: 48,
                height: 48,
                borderRadius: 8,
                backgroundColor: bgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {Icon ? <Icon /> : <LockIcon size={24} color="white" />}
            </Box>
            <Box>
              <Text size="4" weight="bold" style={{ display: "block" }}>
                {def.display_name}
              </Text>
              <Text size="2" color="gray" style={{ display: "block" }}>
                {def.description}
              </Text>
              <Flex gap="2" mt="2">
                {isConnected ? (
                  <Badge variant="soft" color="green" size="1">
                    <CheckCircleIcon size={12} style={{ marginRight: 4 }} />{" "}
                    Connected
                  </Badge>
                ) : isConfigured ? (
                  <Badge variant="soft" color="blue" size="1">
                    OAuth Configured
                  </Badge>
                ) : (
                  <Badge variant="soft" color="gray" size="1">
                    <AlertCircleIcon size={12} style={{ marginRight: 4 }} /> Not
                    Configured
                  </Badge>
                )}
              </Flex>
            </Box>
          </Flex>
          <Flex gap="2">
            {isConnected ? (
              <>
                <Button
                  variant="soft"
                  size="2"
                  onClick={() => handleReconnect(def)}
                >
                  <RefreshCwIcon size={14} /> Reconnect
                </Button>
                <Button
                  variant="soft"
                  color="red"
                  size="2"
                  onClick={() => deleteMutation.mutate(connection.id)}
                  disabled={deleteMutation.isPending}
                >
                  <TrashIcon size={14} /> Disconnect
                </Button>
                <Button
                  variant="soft"
                  size="2"
                  onClick={() => handleOpenConfig(def)}
                >
                  Edit OAuth
                </Button>
              </>
            ) : isConfigured ? (
              <>
                <Button size="2" onClick={() => startOAuthFlow(def.provider)}>
                  <ExternalLinkIcon size={14} /> Connect
                </Button>
                <Button
                  variant="soft"
                  size="2"
                  onClick={() => handleOpenConfig(def)}
                >
                  Edit OAuth
                </Button>
              </>
            ) : (
              <Button size="2" onClick={() => handleOpenConfig(def)}>
                Configure & Connect
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <PageTemplate title="Integrations">
        <Flex justify="center" align="center" py="8">
          <Spinner size="3" />
        </Flex>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title="Integrations"
      description="Manage social sign-in and external service connections"
    >
      <Tabs.Root
        defaultValue={initialTab}
        onValueChange={(tab) => setSearchParams({ tab })}
      >
        <Tabs.List size="2">
          <Tabs.Trigger value="social">Social Sign-In</Tabs.Trigger>
          <Tabs.Trigger value="integrations">Integrations</Tabs.Trigger>
        </Tabs.List>

        <Box pt="4">
          <Tabs.Content value="social">
            <Flex justify="between" align="start" mb="4" gap="4">
              <Box style={{ flex: 1 }}>
                <Text
                  size="2"
                  color="gray"
                  style={{ display: "block", marginBottom: 8 }}
                >
                  Enable social authentication providers so users can sign in
                  with their existing accounts.
                </Text>
                <Box
                  p="2"
                  style={{ backgroundColor: "var(--gray-3)", borderRadius: 4 }}
                >
                  <Flex justify="between" align="start">
                    <Box style={{ flex: 1 }}>
                      <Text
                        size="1"
                        weight="medium"
                        color="gray"
                        style={{ display: "block", marginBottom: 2 }}
                      >
                        Callback URL (add this to each provider's redirect URI
                        settings)
                      </Text>
                      <Text
                        size="1"
                        style={{
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          display: "block",
                          userSelect: "all",
                        }}
                      >
                        {`${window.location.origin}/org/${tenantId}/auth/{provider}/callback`}
                      </Text>
                      <Text
                        size="1"
                        color="gray"
                        mt="1"
                        style={{ display: "block" }}
                      >
                        Replace {`{provider}`} with: google, linkedin, github,
                        or apple
                      </Text>
                    </Box>
                    <Button
                      variant="ghost"
                      size="1"
                      style={{ flexShrink: 0 }}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/org/${tenantId}/auth/{provider}/callback`,
                        );
                        toast.success("Copied to clipboard");
                      }}
                    >
                      <CopyIcon size={14} />
                    </Button>
                  </Flex>
                </Box>
                <Callout.Root color="blue" size="1" mt="2">
                  <Callout.Icon>
                    <InfoIcon size={14} />
                  </Callout.Icon>
                  <Callout.Text>
                    Most OAuth apps only allow one callback URL each. To use
                    the same provider for both social sign-in and integration
                    connections (e.g. read repo data on GitHub), create a{" "}
                    <strong>separate OAuth app</strong> for the integration
                    and configure it under the Integrations tab.
                  </Callout.Text>
                </Callout.Root>
              </Box>
              <Button
                variant="outline"
                size="2"
                onClick={() =>
                  window.open(
                    `/org/${tenantId}/auth/test-login`,
                    "_blank",
                    "width=480,height=700",
                  )
                }
                style={{ flexShrink: 0 }}
              >
                Preview Sign-In Page
              </Button>
            </Flex>
            <Flex direction="column" gap="3">
              {socialProviders.map(renderSocialCard)}
            </Flex>
          </Tabs.Content>

          <Tabs.Content value="integrations">
            <Text size="2" color="gray" mb="4" style={{ display: "block" }}>
              Connect external services to use in workflows.
            </Text>
            <Flex direction="column" gap="3">
              {workflowIntegrations.map(renderIntegrationCard)}
            </Flex>
          </Tabs.Content>
        </Box>
      </Tabs.Root>

      <ConfigDialog
        definition={selectedDefinition}
        status={
          selectedDefinition
            ? getOAuthStatus(selectedDefinition.provider) ?? null
            : null
        }
        open={showOAuthDialog}
        onOpenChange={setShowOAuthDialog}
        onAfterSave={handleAfterSave}
        callbackUrl={
          selectedDefinition?.can_social_sign_in
            ? `${window.location.origin}/org/${tenantId}/auth/${selectedDefinition?.provider}/callback`
            : integrationCallbackUrl
        }
        setupGuide={
          selectedDefinition
            ? setupGuides[selectedDefinition.provider]
            : undefined
        }
      />
    </PageTemplate>
  );
});

export default IntegrationsPage;
